import {
  AnchorButton,
  Colors,
  Icon,
  Intent,
  NonIdealState,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { createSelector } from '@reduxjs/toolkit';
import classNames from 'classnames';
import * as React from 'react';
import { alphaSort, isEqual, numericSort } from 'vet-bones/utils';

import { Link } from 'src/components/Link';
import { getProjectDiagramModelTableCellLinkTestId } from 'src/constants/testIds';
import { modelFieldsSelector } from 'src/containers/ProjectDiagram/components/modelList/modelFieldsSelector';
import { ProjectDiagramModelTable } from 'src/containers/ProjectDiagram/components/modelList/ProjectDiagramModelTable';
import {
  RelationshipTableItem,
  TableField,
} from 'src/containers/ProjectDiagram/components/modelList/types';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useClickModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptRelationshipLink';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getRelationshipDomainsAndRanges } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/getRelationshipDomainsAndRanges';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { projectHiddenModelsSelector } from 'src/selectors/projectHiddenModels';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectModelIdsByIriSelector } from 'src/selectors/projectModelsIdsByIri';
import { ModelListTab, ModelListTableData } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const INITIAL_FIELDS: Array<TableField<RelationshipTableItem>> = [
  {
    id: 'name',
    label: copy.components.projectDiagram.modelList.relationships.name,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<RelationshipTableItem>(
        (item) => item.name.value,
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'name',
  },
  {
    cellClassName: 'sd-project-diagram-model-table-cell--monospace',
    hidden: true,
    id: 'iri',
    label: copy.components.projectDiagram.modelList.relationships.iri,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<RelationshipTableItem>(
        (item) => item.iri.value,
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'iri',
  },
  {
    id: 'domains',
    label: copy.components.projectDiagram.modelList.relationships.domains,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<RelationshipTableItem>(
        (item) => item.domains.value.map((domain) => domain.label).join(', '),
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'domains',
  },
  {
    id: 'ranges',
    label: copy.components.projectDiagram.modelList.relationships.ranges,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<RelationshipTableItem>(
        (item) => item.ranges.value.map((range) => range.label).join(', '),
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'ranges',
  },
];

const relationshipTableItemsSelector = (projectId: string) =>
  createSelector(
    entityDrawerSelector,
    projectLabelsByIriSelector(projectId),
    projectLinkDataArraySelector(projectId),
    projectModelIdsByIriSelector(projectId),
    projectHiddenModelsSelector(projectId),
    modelFieldsSelector<RelationshipTableItem>(projectId),
    (
      entityDrawer,
      labelsByIri,
      linkDataArray,
      modelIdsByIri,
      hiddenModelIds,
      modelFields
    ) =>
      Array.from(
        linkDataArray.reduce((acc, data) => {
          if (isModelConceptRelationship(data)) {
            acc.add(data.value);
          }
          return acc;
        }, new Set<string>())
      )
        .map((relationshipIri) => {
          const { domains, ranges } = getRelationshipDomainsAndRanges(
            linkDataArray,
            relationshipIri
          );
          return {
            hidden: Boolean(
              modelIdsByIri[relationshipIri]?.every((model) =>
                hiddenModelIds?.includes(model)
              )
            ),
            id: relationshipIri,
            name: {
              label: null,
              selected: entityDrawer.entityValue === relationshipIri,
              value: getLabelForIri(
                labelsByIri,
                relationshipIri,
                copy.components.projectDiagram.modelConceptRelationship.new
              ),
            },
            iri: {
              label: null,
              value: relationshipIri,
            },
            domains: {
              label: null,
              selected:
                entityDrawer.entityValue &&
                domains.has(entityDrawer.entityValue),
              value: Array.from(domains)
                .map((domain) => ({
                  hidden: Boolean(
                    modelIdsByIri[domain]?.every((model) =>
                      hiddenModelIds?.includes(model)
                    )
                  ),
                  id: domain,
                  label: getLabelForIri(
                    labelsByIri,
                    domain,
                    copy.components.projectDiagram.modelConceptClass.new
                  ),
                }))
                .sort(alphaSort((domain) => domain.label)),
            },
            ranges: {
              label: null,
              selected:
                entityDrawer.entityValue &&
                ranges.has(entityDrawer.entityValue),
              value: Array.from(ranges)
                .map((range) => ({
                  hidden: Boolean(
                    modelIdsByIri[range]?.every((model) =>
                      hiddenModelIds?.includes(model)
                    )
                  ),
                  id: range,
                  label: getLabelForIri(
                    labelsByIri,
                    range,
                    copy.components.projectDiagram.modelConceptClass.new
                  ),
                }))
                .sort(alphaSort((range) => range.label)),
            },
            ...Object.fromEntries(
              modelFields.map((field) => [
                field.value,
                {
                  label: null,
                  value: {
                    checked: modelIdsByIri[relationshipIri]?.includes(
                      field.value
                    ),
                    hidden:
                      !field.value || hiddenModelIds?.includes(field.value),
                  },
                },
              ])
            ),
          } as RelationshipTableItem;
        })
        .sort(alphaSort((item) => item.name.value))
  );

export const ProjectDiagramModelRelationships: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const modelFields = useReduxSelector(
    modelFieldsSelector<RelationshipTableItem>(projectId),
    isEqual
  );
  const relationshipItems = useReduxSelector(
    relationshipTableItemsSelector(projectId),
    isEqual
  );

  const handleClickClass = useClickModelConceptClassLink();
  const handleClickRelationship = useClickModelConceptRelationshipLink();

  const tableData = useReduxSelector(
    (state) => state.ui.modelList.tableData[ModelListTab.RELATIONSHIPS]
  );
  const handleUpdateTableData = (
    data: ModelListTableData<RelationshipTableItem>
  ) => {
    dispatch(
      uiSlice.actions.updatedModelListTableData({
        tab: ModelListTab.RELATIONSHIPS,
        data,
      })
    );
  };

  const relationshipFields = React.useMemo(
    () =>
      INITIAL_FIELDS.concat(
        modelFields.map((field) => ({
          ...field,
          sorter: (direction: 'asc' | 'desc') =>
            numericSort<RelationshipTableItem>(
              (item) => (item[field.value]?.value.checked ? 1 : -1),
              direction === 'desc'
            ),
        }))
      ),
    [modelFields]
  );

  const relationshipItemsWithLinks = React.useMemo(
    () =>
      relationshipItems.map(
        ({ hidden, id, name, iri, domains, ranges, ...models }, idx) =>
          ({
            hidden,
            id,
            name: {
              label: (
                <>
                  <Link
                    className={classNames(
                      'sd-project-diagram-model-table-link',
                      { 'sd-project-diagram-model-table-link--weak': hidden }
                    )}
                    onClick={() => {
                      handleClickRelationship({
                        text: name.value,
                        value: id,
                      });
                    }}
                    testId={getProjectDiagramModelTableCellLinkTestId(id, idx)}
                  >
                    {name.value}
                  </Link>
                </>
              ),
              selected: name.selected,
              value: name.value,
            },
            iri: {
              label: iri.value,
              value: iri.value,
            },
            domains: {
              label: (
                <>
                  {domains.value.map((domain, domainIdx) => (
                    <React.Fragment key={domain.id}>
                      {domainIdx ? ', ' : ''}
                      <Link
                        className={classNames(
                          'sd-project-diagram-model-table-link',
                          {
                            'sd-project-diagram-model-table-link--weak':
                              domain.hidden,
                          }
                        )}
                        onClick={() => {
                          handleClickClass({
                            text: domain.label,
                            value: domain.id,
                          });
                        }}
                      >
                        {domain.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </>
              ),
              selected: domains.selected,
              value: domains.value,
            },
            ranges: {
              label: (
                <>
                  {ranges.value.map((range, rangeIdx) => (
                    <React.Fragment key={range.id}>
                      {rangeIdx ? ', ' : ''}
                      <Link
                        className={classNames(
                          'sd-project-diagram-model-table-link',
                          {
                            'sd-project-diagram-model-table-link--weak':
                              range.hidden,
                          }
                        )}
                        onClick={() => {
                          handleClickClass({
                            text: range.label,
                            value: range.id,
                          });
                        }}
                      >
                        {range.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </>
              ),
              selected: ranges.selected,
              value: ranges.value,
            },
            ...Object.fromEntries(
              Object.entries(models).map(([modelId, model]) => [
                modelId,
                {
                  label: (
                    <Icon
                      color={
                        !model.value.checked || model.value.hidden
                          ? Colors.GRAY4
                          : undefined
                      }
                      icon={
                        model.value.checked
                          ? IconNames.FULL_CIRCLE
                          : IconNames.CIRCLE
                      }
                    />
                  ),
                  value: model.value,
                },
              ])
            ),
          } as RelationshipTableItem)
      ),
    [relationshipItems, handleClickClass, handleClickRelationship]
  );

  if (!relationshipItemsWithLinks.length) {
    return (
      <NonIdealState
        action={
          <AnchorButton
            intent={Intent.PRIMARY}
            onClick={() =>
              dispatch(
                uiSlice.actions.toggledCreateRelationshipDrawer({
                  isOpen: true,
                })
              )
            }
            text={
              copy.components.projectDiagram.modelList.relationships.emptyState
                .button
            }
          />
        }
        description={
          copy.components.projectDiagram.modelList.relationships.emptyState
            .description
        }
        title={
          copy.components.projectDiagram.modelList.relationships.emptyState
            .title
        }
      />
    );
  }

  return (
    <ProjectDiagramModelTable<RelationshipTableItem>
      fields={relationshipFields}
      items={relationshipItemsWithLinks}
      onUpdateTableData={handleUpdateTableData}
      tableData={tableData}
    />
  );
};
